/**
 * One-click presets for the Exclusions builder. Keeps the UI dumb and the
 * trees consistent (impressions/roi/uniqueClicks are part of the metadata
 * catalog so they resolve in the rule evaluator).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FlowLayer, RuleAction, RuleNode } from '@trafficpulse/shared';

export interface ExcludePreset {
  id: string;
  label: string;
  hint: string;
  build: () => RuleNode;
  /** A preset that is a whole rule: its actions, layer and name. Absent =
   *  the plain exclude condition the chip describes. */
  actions?: RuleAction[];
  appliesTo?: FlowLayer[];
  name?: string;
  /** The window the rule's tracker-metric conditions measure over. */
  dateRange?: string;
}

const condition = (metric: string, op: '<' | '<=' | '>' | '>=', value: number): RuleNode => ({
  kind: 'condition',
  metric,
  op,
  value,
});

/** The performance tag the tag-based reactivate preset asks for — a flow's
 *  own tag rule names it (the Tags tab); the picker lists the flow's tags. */
export const REACTIVATE_TAG = 'BEST';

const tagged = (tagName: string): RuleNode => ({ kind: 'tag', tagName });

const reactivateGroup = (id: string, qualifier: RuleNode): RuleNode => ({
  kind: 'group',
  id,
  op: 'AND',
  children: [condition('capResumable', '>=', 1), qualifier],
});

export const EXCLUDE_PRESETS: ExcludePreset[] = [
  {
    id: 'skip-low-volume',
    label: 'Skip low-volume',
    hint: 'impressions < 100',
    build: () => condition('impressions', '<', 100),
  },
  {
    id: 'skip-unprofitable',
    label: 'Skip unprofitable',
    hint: 'roi < 0',
    build: () => condition('roi', '<', 0),
  },
  {
    id: 'skip-new-items',
    label: 'Skip new items',
    hint: 'uniqueClicks < 20',
    build: () => condition('uniqueClicks', '<', 20),
  },
  // Partner caps (MD-1147): both act on a matched PATH's offers.
  {
    id: 'cap-pause',
    label: 'Pause at offer cap',
    hint: 'capReached ≥ 1 → pause + add new element',
    build: () => condition('capReached', '>=', 1),
    actions: ['pause', 'add-element'],
    appliesTo: ['paths'],
    name: 'Offer cap reached',
  },
  // Reactivate a PAUSED offer once its cap has room again (a day / week /
  // month cap resets on its Danish-calendar boundary) — and only when it performed: the rule's conditions
  // hold per offer. The default gate is the flow's performance tag — the
  // path must carry BEST (tags are path-level, set by the Tags tab rules).
  {
    id: 'cap-reactivate',
    label: 'Reactivate after cap',
    hint: `capResumable ≥ 1 & tag ${REACTIVATE_TAG} → reactivate`,
    build: () => reactivateGroup('cap-reactivate', tagged(REACTIVATE_TAG)),
    actions: ['reactivate'],
    appliesTo: ['paths'],
    name: 'Offer cap free again',
  },
];
